import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/Graphql/apolloClient";
import { GET_OFFLINE_PAYMENT_INVOICE } from "@/Graphql/Queries";
import { generateMockData } from "@/Graphql/MockData";
import JSZip from "jszip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  CheckCircle, 
  Calendar, 
  FileText,
  Download,
  Eye,
  DollarSign,
  Building,
  MapPin,
  AlertCircle
} from "lucide-react";

interface PaymentProofApprovedProps {
  base_path?: string;
}

// Mock data for offline payment campaigns (fallback)
const getOfflinePaymentCampaignData = (campaignId: string) => {
  const campaigns: { [key: string]: any } = {
    "17": {
      id: 17,
      name: "Summer Fashion Showcase",
      code: "E - 2024-08150000017",
      paymentMethod: "offline",
      paymentStatus: "approved",
      startDate: "2024-08-01",
      endDate: "2024-08-31",
      duration: "30 days",
      totalPrice: 12500.00,
      subTotal: 11363.64,
      gst: 1136.36,
      inventoryCount: 2,
      inventories: [
        {
          id: 11,
          name: "Melbourne CBD Digital Tower",
          location: "Melbourne, VIC",
          impressions: 425000,
          price: 6250.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1514395462725-fb4566210144?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "Urban Display Networks"
        },
        {
          id: 12,
          name: "Brisbane Shopping Center Hub",
          location: "Brisbane, QLD",
          impressions: 380000,
          price: 6250.00,
          resolution: "1440 x 900",
          image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "Metro Media Group"
        }
      ],
      offlinePayment: {
        id: 7,
        paymentSlipFilename: "bank-transfer-receipt-aug2024.pdf",
        paymentSlipUrl: "/objects/uploads/payment-proof-1692345678901.pdf",
        submittedDate: "2024-07-25",
        approvedDate: "2024-07-26",
        approvedBy: "Marketing Admin",
        bankReference: "BT240725001",
        invoiceNumber: "INV-2024-001237",
        notes: "Payment verified through Commonwealth Bank transfer"
      },
      billingDetails: {
        companyName: "Fashion Forward Boutique",
        email: "billing@fashionforward.com.au",
        address: "456 Collins Street",
        city: "Melbourne",
        state: "VIC",
        postcode: "3000",
        country: "Australia"
      }
    },
    "18": {
      id: 18,
      name: "Local Restaurant Promotion",
      code: "E - 2024-09010000018",
      paymentMethod: "offline",
      paymentStatus: "approved", 
      startDate: "2024-09-15",
      endDate: "2024-10-15",
      duration: "30 days",
      totalPrice: 8750.00,
      subTotal: 7954.55,
      gst: 795.45,
      inventoryCount: 1,
      inventories: [
        {
          id: 13,
          name: "Sydney Harbour Bridge View",
          location: "Sydney, NSW",
          impressions: 650000,
          price: 8750.00,
          resolution: "2048 x 1152",
          image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "Harbour Media Solutions"
        }
      ],
      offlinePayment: {
        id: 8,
        paymentSlipFilename: "business-payment-confirmation.pdf",
        paymentSlipUrl: "/objects/uploads/payment-proof-1692456789012.pdf",
        submittedDate: "2024-08-28",
        approvedDate: "2024-08-29",
        approvedBy: "Finance Team",
        bankReference: "BP240828002",
        invoiceNumber: "INV-2024-001238",
        notes: "Payment confirmed via ANZ Business Banking"
      },
      billingDetails: {
        companyName: "Taste of Sydney Restaurant",
        email: "admin@tasteofsydney.com.au",
        address: "789 George Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "19": {
      id: 19,
      name: "Tech Conference Advertising",
      code: "E - 2024-10050000019",
      paymentMethod: "offline",
      paymentStatus: "approved",
      startDate: "2024-10-20",
      endDate: "2024-11-20",
      duration: "31 days", 
      totalPrice: 15600.00,
      subTotal: 14181.82,
      gst: 1418.18,
      inventoryCount: 3,
      inventories: [
        {
          id: 14,
          name: "Perth CBD Innovation Hub",
          location: "Perth, WA",
          impressions: 290000,
          price: 5200.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "West Coast Digital"
        },
        {
          id: 15,
          name: "Adelaide Tech District",
          location: "Adelaide, SA",
          impressions: 220000,
          price: 5200.00,
          resolution: "1600 x 900",
          image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "South Media Networks"
        },
        {
          id: 16,
          name: "Darwin Business Centre",
          location: "Darwin, NT",
          impressions: 180000,
          price: 5200.00,
          resolution: "1440 x 810",
          image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=400&h=300&fit=crop",
          type: "Digital",
          mediaOwner: "Northern Displays"
        }
      ],
      offlinePayment: {
        id: 9,
        paymentSlipFilename: "corporate-payment-receipt.pdf",
        paymentSlipUrl: "/objects/uploads/payment-proof-1692567890123.pdf",
        submittedDate: "2024-09-15",
        approvedDate: "2024-09-16",
        approvedBy: "Campaign Manager",
        bankReference: "CP240915003",
        invoiceNumber: "INV-2024-001239",
        notes: "Corporate payment processed through Westpac Business"
      },
      billingDetails: {
        companyName: "TechInnovate Solutions Pty Ltd",
        email: "finance@techinnovate.com.au", 
        address: "321 Innovation Drive",
        city: "Perth",
        state: "WA",
        postcode: "6000",
        country: "Australia"
      }
    }
  };

  return campaigns[campaignId] || null;
};

export default function PaymentProofApproved({ base_path = "" }: PaymentProofApprovedProps) {
  const navigate = useNavigate();
  const { campaignId: paramCampaignId } = useParams<{ campaignId: string }>();

  // Get campaign ID from URL params or query params
  let campaignId = paramCampaignId;
  if (!campaignId) {
    const urlParams = new URLSearchParams(window.location.search);
    campaignId = urlParams.get('campaign') || undefined;
  }

  // State for data and loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [data, setData] = useState<any>(null);

  // Get access token
  const token = localStorage.getItem('authToken') || '';

  console.log('PaymentProofApproved - Campaign ID:', campaignId);
  console.log('PaymentProofApproved - Token:', token ? 'Present' : 'Missing');

  // Fetch offline payment invoice data
  useEffect(() => {
    const fetchOfflinePaymentInvoice = async () => {
      if (!campaignId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(GET_OFFLINE_PAYMENT_INVOICE);
          setData(mockResponse.data);
          setError(null);
        } else {
          const { data } = await client.query({
            query: GET_OFFLINE_PAYMENT_INVOICE,
            variables: {
              accessToken: token,
              campaignId: campaignId || '',
              status: 'APPROVED'
            },
            fetchPolicy: 'network-only'
          });
          setData(data);
          setError(null);
        }
      } catch (err) {
        setError(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOfflinePaymentInvoice();
  }, [campaignId, token]);

  console.log('PaymentProofApproved - Query State:', { loading, error, data });

  const offlineInvoiceData = data?.offlinePaymentInvoice;
  const paymentTransaction = offlineInvoiceData?.paymentTransaction;
  const campaign = offlineInvoiceData?.campaign;
  const assets = offlineInvoiceData?.assets || [];

  // Map campaign data from API response
  const campaignData = campaign ? {
    id: campaign.id,
    name: campaign.name,
    code: campaign.dealId || `Campaign ${campaign.id}`,
    paymentMethod: paymentTransaction?.paymentProvider?.toLowerCase() || 'offline',
    paymentStatus: paymentTransaction?.status?.toLowerCase() || 'approved',
    startDate: campaign.startDate?.date || '',
    endDate: campaign.endDate?.date || '',
    duration: calculateDuration(campaign.startDate?.date, campaign.endDate?.date),
    totalPrice: campaign.priceSummary?.netTotal || 0,
    subTotal: campaign.priceSummary?.subTotal || 0,
    gst: campaign.priceSummary?.tax?.value || 0,
    taxName: campaign.priceSummary?.tax?.name || 'GST',
    taxPercent: campaign.priceSummary?.tax?.percent || 0,
    currencyCode: campaign.priceSummary?.currency?.code || 'INR',
    currencySymbol: campaign.priceSummary?.currency?.symbol || 'INR',
    inventoryCount: (campaign.inventoriesSummary?.classicInventories || 0) +
                    (campaign.inventoriesSummary?.digitalInventories || 0) +
                    (campaign.inventoriesSummary?.packages || 0),
    inventories: (campaign.campaignInventories || []).map((inv: any) => ({
      id: inv.inventoryId,
      name: inv.inventoryName,
      location: inv.inventoryVenueType || 'N/A',
      impressions: inv.inventoryReports?.totalPotentialViews || 0,
      price: inv.inventoryPrice || 0,
      resolution: inv.inventoryResolutions || 'N/A',
      image: inv.inventoryThumbnailUrl || 'https://via.placeholder.com/400x300',
      type: mapInventoryType(inv.inventoryType),
      mediaOwner: campaign.company?.name || 'N/A',
      isSpotBased: inv.isSpotBased
    })),
    offlinePayment: {
      id: paymentTransaction?.id || '',
      paymentSlipFilename: assets[0]?.fileName || 'payment-proof.png',
      paymentSlipUrl: assets[0]?.filePath || '',
      submittedDate: paymentTransaction?.createdDate || '',
      approvedDate: paymentTransaction?.lastModifiedDate || '',
      approvedBy: 'System Admin',
      bankReference: paymentTransaction?.transactionId || '',
      invoiceNumber: offlineInvoiceData?.referenceInvoiceNumber || '',
      notes: `Payment verified through ${paymentTransaction?.providerMetadata?.bankName || 'bank'} transfer`,
      bankDetails: paymentTransaction?.providerMetadata
    },
    billingDetails: {
      companyName: campaign.buyerDetails?.companyName || campaign.company?.name || 'N/A',
      email: campaign.buyerDetails?.email || campaign.user?.name || 'N/A',
      address: campaign.buyerDetails?.address || 'N/A',
      city: '',
      state: '',
      postcode: '',
      country: campaign.priceSummary?.currency?.country || 'N/A'
    },
    assets: assets
  } : null;

  // Helper function to calculate duration
  function calculateDuration(startDate: string, endDate: string): string {
    if (!startDate || !endDate) return 'N/A';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  }

  // Helper function to map inventory types
  function mapInventoryType(type: string): string {
    switch (type?.toUpperCase()) {
      case 'DIGITAL_BILLBOARD':
        return 'Digital';
      case 'STATIC_BILLBOARD':
      case 'CLASSICAL_BILLBOARD':
        return 'Classic';
      case 'PACKAGE':
        return 'Package';
      case 'TRANSIT':
        return 'Transit';
      default:
        return 'Digital';
    }
  }

  const formatCurrency = (amount: number) => {
    const currencyCode = campaignData?.currencyCode || 'AUD';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPaymentMethod = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'stripe': return 'Stripe';
      case 'offline': return 'Offline';
      case 'paypal': return 'PayPal';
      case 'payoneer': return 'Payoneer';
      default: return 'Offline';
    }
  };

  const handleDownloadPaymentProof = async () => {
    // Download all payment proof assets as a zip file
    const assets = campaignData?.assets || [];
    
    if (assets.length === 0) {
      alert('No payment proof files available for download');
      return;
    }
    
    try {
      const zip = new JSZip();
      const folder = zip.folder('payment-proofs');
      
      // Fetch all assets and add to zip
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        try {
          const response = await fetch(asset.filePath);
          const blob = await response.blob();
          const fileName = asset.fileName || `payment-proof-${i + 1}.png`;
          folder?.file(fileName, blob);
        } catch (error) {
          console.error(`Failed to fetch asset ${i + 1}:`, error);
        }
      }
      
      // Generate zip file and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payment-proof-${campaignData?.code || campaignData?.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Zip creation failed:', error);
      alert('Failed to create zip file. Please try again.');
    }
  };

  const handleViewInvoice = () => {
    navigate(`${base_path}/payment-invoice?campaign=${campaignData?.id}`);
  };

  const handleViewInvoiceSlip = () => {
    navigate(`${base_path}/payment-invoice-view?campaign=${campaignData?.id}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading payment proof details...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-900 font-semibold mb-2">Error Loading Payment Proof</p>
              <p className="text-gray-600 mb-4">{error.message}</p>
              <Button onClick={() => navigate(`${base_path}/my-campaigns`)} className="mt-4">
                Back to Campaigns
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // No data state
  if (!campaignId || !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">Campaign not found or invalid payment method.</p>
              <Button onClick={() => navigate(`${base_path}/my-campaigns`)} className="mt-4">
                Back to Campaigns
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header with Back Button on Right */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-emerald-600">Payment Proof Approved</h1>
            <p className="text-gray-600">Campaign: {campaignData.name}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate(`${base_path}/my-campaigns`)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Campaigns
          </Button>
        </div>

        {/* Status Banner */}
        <Card className="mb-8 border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-green-800"> Payment Approved Successfully</h2>
                <p className="text-green-700">
                  Your offline payment has been verified and approved. Your campaign is now active.
                </p>
                <div className="flex items-center gap-6 mt-3 text-sm text-green-600">
                  <span>Approved on {formatDate(campaignData.offlinePayment.approvedDate)}</span>
                  <span>•</span>
                  <span>Approved by {campaignData.offlinePayment.approvedBy}</span>
                  <span>•</span>
                  <span>Invoice: {campaignData.offlinePayment.invoiceNumber}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Actions Section - Moved to Top */}
            <Card>
              <CardHeader>
                <CardTitle className="text-emerald-600">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-3">
                <Button 
                  onClick={handleViewInvoice}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Payment Invoice
                </Button>
                
                <Button 
                  onClick={handleViewInvoiceSlip}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Invoice Slip
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleDownloadPaymentProof}
                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Payment Proof
                </Button>
              </CardContent>
            </Card>

            {/* Payment Proof Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <FileText className="h-5 w-5" />
                  Payment Proof Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Method</label>
                    <p className="font-medium">Offline Bank Transfer</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bank Reference</label>
                    <p className="font-medium">{campaignData.offlinePayment.bankReference}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Submitted Date</label>
                    <p className="font-medium">{formatDate(campaignData.offlinePayment.submittedDate)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Approved Date</label>
                    <p className="font-medium">{formatDate(campaignData.offlinePayment.approvedDate)}</p>
                  </div>
                </div>

                <Separator />

                <div>
                  <label className="text-sm font-medium text-gray-500">Payment File</label>
                  <div className="flex items-start gap-4 mt-3">
                    <div className="flex-shrink-0 bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4">
                      <FileText className="h-16 w-16 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-lg">{campaignData.offlinePayment.paymentSlipFilename}</p>
                      <p className="text-sm text-gray-500 mt-1">Payment proof document</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Uploaded: {formatDate(campaignData.offlinePayment.submittedDate)}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleDownloadPaymentProof}
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 flex-shrink-0"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>

                {campaignData.offlinePayment.notes && (
                  <>
                    <Separator />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Admin Notes</label>
                      <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {campaignData.offlinePayment.notes}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Campaign Inventory */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <Building className="h-5 w-5" />
                  Campaign Inventory ({campaignData.inventoryCount} {campaignData.inventoryCount === 1 ? 'Billboard' : 'Billboards'})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {campaignData.inventories.map((inventory: any) => (
                    <div key={inventory.id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <img 
                        src={inventory.image} 
                        alt={inventory.name}
                        className="w-20 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium">{inventory.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <MapPin className="h-3 w-3" />
                          {inventory.location}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-600">{inventory.resolution}</span>
                          <span className="text-gray-600">•</span>
                          <span className="text-gray-600">{inventory.impressions.toLocaleString()} impressions</span>
                          <span className="text-gray-600">•</span>
                          <span className="text-gray-600">{inventory.mediaOwner}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                          {inventory.type}
                        </Badge>
                        <p className="font-medium text-lg mt-1">{formatCurrency(inventory.price)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Campaign Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-emerald-600">Campaign Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Campaign</label>
                  <p className="font-medium">{campaignData.name}</p>
                  <p className="text-sm text-gray-600">{campaignData.code}</p>
                </div>

                <Separator />

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Campaign Period</p>
                    <p className="font-medium">{formatDate(campaignData.startDate)} - {formatDate(campaignData.endDate)}</p>
                    <p className="text-sm text-gray-600">{campaignData.duration}</p>
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="text-sm font-medium text-gray-500">Payment Method</p>
                  <p className="font-medium text-emerald-600">{formatPaymentMethod(campaignData.paymentMethod)}</p>
                </div>

                <Separator />

                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Total Amount</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(campaignData.totalPrice)}</p>
                    <div className="text-sm text-gray-600 mt-1">
                      <p>Subtotal: {formatCurrency(campaignData.subTotal)}</p>
                      <p>GST: {formatCurrency(campaignData.gst)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>



            {/* Billing Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-emerald-600">Billing Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <p className="font-medium">{campaignData.billingDetails.companyName}</p>
                  <p className="text-sm text-gray-600">{campaignData.billingDetails.email}</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p>{campaignData.billingDetails.address}</p>
                  <p>{campaignData.billingDetails.city}, {campaignData.billingDetails.state} {campaignData.billingDetails.postcode}</p>
                  <p>{campaignData.billingDetails.country}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}